/**
 * Signal: a night vision mode laid over the storefront without touching it.
 *
 * - Three `popover="manual"` layers in the top layer tint everything through blend modes (color,
 *   multiply, screen; backdrop-filter isn't reliable in every engine). They are raised again
 *   whenever a popover or dialog of the theme opens, so drawers and sheets are tinted too.
 * - A small fixed panel holds Pause Music and Exit Night Vision; music plays at 30 %.
 * - Unlocking by typing plays a terminal intro. `?callsign=<token>` opens the mode without it
 *   (links for friends). The mode then lasts for the tab's session.
 *
 * Loaded from layout/theme.liquid, never in the theme editor. The stylesheet and the music load
 * only once the mode is on. Nothing here is a theme setting: CALLSIGN holds hashes only, written
 * by `node .src/tools/signal-callsign.mjs <word>`. Never put the word itself into the repo.
 */

// [length of the word, hash of the word, hash of the link token]
const CALLSIGN = [7, "s5kr048q52", "1vwyfwu522u"];
const PARAM = "callsign";
const VOLUME = 0.3;
const SALT = "bia:signal:";
const ROUNDS = 16384;

/** cyrb53 (bryc, public domain): a fast 53-bit string hash. */
function cyrb53(text, seed) {
	let h1 = 0xdeadbeef ^ seed;
	let h2 = 0x41c6ce57 ^ seed;
	for (let index = 0; index < text.length; index += 1) {
		const code = text.charCodeAt(index);
		h1 = Math.imul(h1 ^ code, 2654435761);
		h2 = Math.imul(h2 ^ code, 1597334677);
	}
	h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
	h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
	h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
	h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
	return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}

/** Salted and stretched: every guess costs 16384 rounds (about 10 ms), and a check runs at most once per keystroke. */
export function digest(text) {
	let hash = text;
	for (let round = 0; round < ROUNDS; round += 1) hash = cyrb53(SALT + hash, round).toString(36);
	return hash;
}

export const linkToken = (word) => digest(`link:${word}`);
export const tokenCheck = (token) => digest(`check:${token}`);

const isWord = (text) => text.length === CALLSIGN[0] && digest(text) === CALLSIGN[1];
const isToken = (token) => /^[0-9a-z]{1,12}$/.test(token ?? "") && tokenCheck(token) === CALLSIGN[2];

// Node imports this file for the hashes (.src/tools/signal-callsign.mjs); everything below needs a page.
if (globalThis.document) start();

function start() {
	const assets = document.querySelector("script[data-signal-style]")?.dataset ?? {};
	const reducedMotion = matchMedia("(prefers-reduced-motion: reduce)");
	const state = { token: null, run: 0, layers: [], hud: null, intro: null, clock: 0, raise: 0 };
	let typed = "";
	let style = null;

	const store = {
		get: (key) => {
			try {
				return sessionStorage.getItem(`signal:${key}`);
			} catch {
				return null;
			}
		},
		set: (key, value) => {
			try {
				sessionStorage.setItem(`signal:${key}`, value);
			} catch {
				// No storage (private mode, blocked): the mode lasts for this page only.
			}
		},
		remove: (key) => {
			try {
				sessionStorage.removeItem(`signal:${key}`);
			} catch {
				// See above.
			}
		},
	};

	const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
	const isOwn = (element) => Boolean(element?.closest?.(".signal-layer, .signal-intro"));

	/* ---------- Unlock ---------- */

	function toggle(token) {
		if (state.token) deactivate();
		else activate(token, { intro: true });
	}

	// Typing anywhere outside a field. Only the last few keys are kept: as many as the word has.
	addEventListener(
		"keydown",
		(event) => {
			if (state.intro || event.repeat || event.isComposing || event.ctrlKey || event.metaKey || event.altKey) return;
			if (event.key.length !== 1 || event.target.closest?.("input, textarea, select, [contenteditable]")) return;
			typed = (typed + event.key.toLowerCase()).slice(-CALLSIGN[0]);
			if (!isWord(typed)) return;
			const word = typed;
			typed = "";
			toggle(linkToken(word));
		},
		true,
	);

	// Typing into a search or text field: the only way in on a phone. Only a field holding nothing
	// but the word counts, so a shopper's longer query is never hashed and never cut. It is emptied.
	addEventListener(
		"input",
		(event) => {
			const field = event.target;
			if (event.isComposing || !(field instanceof HTMLInputElement) || !["search", "text"].includes(field.type)) return;
			const word = field.value.trim().toLowerCase();
			if (!isWord(word)) return;
			toggle(linkToken(word));
			field.value = "";
			field.dispatchEvent(new Event("input", { bubbles: true }));
			field.blur();
		},
		true,
	);

	/* ---------- Mode ---------- */

	async function activate(token, { intro = false, warm = false } = {}) {
		if (state.token) return;
		state.token = token;
		const run = ++state.run;
		store.set("token", token);
		syncUrl();
		if (intro) {
			store.remove("music");
			store.remove("time");
		}
		// Before the first await: an unlock by typing still counts as the user's gesture here.
		if (store.get("music") !== "off") startMusic();

		// Without its stylesheet a layer would show as a bare popover box: then the mode stays off.
		if (!(await loadStyle())) {
			deactivate();
			return;
		}
		if (!document.body) await new Promise((resolve) => addEventListener("DOMContentLoaded", resolve, { once: true }));
		if (run !== state.run) return;

		mountLayers();
		tick();
		state.clock = setInterval(tick, 1000);
		if (intro) await playIntro();
		if (run !== state.run) return;
		mountHud();
		if (intro || warm) warmUp();
		// The intro took focus into its dialog; hand it to the new panel instead of leaving it on <body>.
		if (intro) state.hud.querySelector(".signal-control")?.focus({ preventScroll: true });
	}

	function deactivate() {
		if (!state.token) return;
		state.token = null;
		state.run += 1;
		store.remove("token");
		store.remove("music");
		store.remove("time");
		syncUrl();
		stopMusic();
		state.intro?.close();
		clearInterval(state.clock);
		observer.disconnect();

		const hadFocus = state.hud?.contains(document.activeElement);
		const parts = [...state.layers, state.hud].filter(Boolean);
		state.layers = [];
		state.hud = null;
		for (const part of parts) part.classList.add("is-leaving");
		setTimeout(() => parts.forEach((part) => part.remove()), reducedMotion.matches ? 0 : 700);
		if (hadFocus) document.getElementById("main-content")?.focus({ preventScroll: true });
	}

	// Keeps ?callsign= in the address bar while the mode is on, so the current page can be shared.
	function syncUrl() {
		const url = new URL(location.href);
		if (state.token) {
			if (url.searchParams.get(PARAM) === state.token) return;
			url.searchParams.set(PARAM, state.token);
		} else {
			if (!url.searchParams.has(PARAM)) return;
			url.searchParams.delete(PARAM);
		}
		history.replaceState(history.state, "", url);
	}

	// Resolves whether the stylesheet arrived; a failed one is tried again next time.
	function loadStyle() {
		style ??= new Promise((resolve) => {
			const link = document.createElement("link");
			link.rel = "stylesheet";
			link.href = assets.signalStyle;
			// Chromium holds the first paint for it while the page is still loading (a session in the mode).
			if (document.readyState === "loading") link.setAttribute("blocking", "render");
			link.addEventListener("load", () => resolve(true), { once: true });
			link.addEventListener(
				"error",
				() => {
					link.remove();
					style = null;
					resolve(false);
				},
				{ once: true },
			);
			document.head.append(link);
		});
		return style;
	}

	/* ---------- Layers ---------- */

	const LAYERS = [
		["tone", ""],
		["glass", ""],
		["glow", '<div class="signal-frame"></div>'],
	];

	function mountLayers() {
		state.layers = LAYERS.map(([name, markup]) => {
			const layer = document.createElement("div");
			layer.className = `signal-layer signal-${name}`;
			layer.popover = "manual";
			layer.setAttribute("aria-hidden", "true");
			layer.innerHTML = markup;
			document.body.append(layer);
			layer.showPopover();
			return layer;
		});
		observer.observe(document.documentElement, { attributes: true, attributeFilter: ["open"], subtree: true });
	}

	// Whatever enters the top layer later sits above the layers: put them back on top before the next paint.
	function raise() {
		cancelAnimationFrame(state.raise);
		state.raise = requestAnimationFrame(() => {
			for (const layer of state.layers) {
				if (layer.matches(":popover-open")) layer.hidePopover();
				layer.showPopover();
			}
		});
	}

	document.addEventListener(
		"beforetoggle",
		(event) => {
			if (state.layers.length && event.newState === "open" && !isOwn(event.target)) raise();
		},
		true,
	);

	// Modal dialogs: not every engine fires beforetoggle for them yet.
	const observer = new MutationObserver((records) => {
		if (records.some(({ target }) => target.localName === "dialog" && target.open && !isOwn(target))) raise();
	});

	function warmUp() {
		if (reducedMotion.matches) return;
		const glow = state.layers.at(-1);
		glow?.classList.add("is-warming");
		setTimeout(() => glow?.classList.remove("is-warming"), 1800);
	}

	function tick() {
		const time = `${new Date().toISOString().slice(11, 19)}Z`;
		for (const clock of document.querySelectorAll("[data-signal-clock]")) clock.textContent = time;
	}

	/* ---------- Panel ---------- */

	function mountHud() {
		const hud = document.createElement("div");
		hud.className = "signal-hud";
		hud.setAttribute("role", "group");
		hud.setAttribute("aria-label", "Night Vision");
		hud.innerHTML = `
			<p class="signal-readout" aria-hidden="true"><span class="signal-dot"></span>NV-4 · Gen III · <span data-signal-clock></span></p>
			<button class="signal-control" type="button" data-signal-music>
				<span class="signal-bars" aria-hidden="true"><i></i><i></i><i></i><i></i></span>
				<span class="signal-label">Play Music</span>
			</button>
			<button class="signal-control" type="button" data-signal-exit>
				<svg class="signal-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3.5v7.5"/><path d="M6.7 6.9a7.5 7.5 0 1 0 10.6 0"/></svg>
				<span class="signal-label">Exit Night Vision</span>
			</button>`;
		hud.addEventListener("click", (event) => {
			if (event.target.closest("[data-signal-exit]")) deactivate();
			else if (event.target.closest("[data-signal-music]")) {
				if (sound.on) pauseMusic();
				else {
					store.remove("music");
					startMusic();
				}
			}
		});
		document.body.append(hud);
		state.hud = hud;
		tick();
		renderHud();
	}

	function renderHud() {
		if (!state.hud) return;
		state.hud.classList.toggle("is-playing", sound.on);
		state.hud.querySelector("[data-signal-music] .signal-label").textContent = sound.on ? "Pause Music" : "Play Music";
	}

	/* ---------- Intro ---------- */

	const HEX = () =>
		`0x${Math.floor(Math.random() * 0xffff)
			.toString(16)
			.toUpperCase()
			.padStart(4, "0")}`;

	const STEPS = [
		["cmd", "ssh operator@bia-net --secure"],
		["row", "Establishing uplink", "OK"],
		["row", "Verifying callsign", "OK"],
		["row", `Handshake ${HEX()}-${HEX().slice(2)}`, "OK"],
		["cmd", "mount /dev/nv0 --gen 3"],
		["row", "Image intensifier", "Online"],
		["row", "Phosphor P43", "Calibrated"],
		["gain", "Gain", 84],
		["cmd", "Visibility --level 30"],
		["row", "Audio channel 2", "Live"],
		["cmd", "unlock night-vision"],
	];

	function playIntro() {
		return new Promise((resolve) => {
			const dialog = document.createElement("dialog");
			dialog.className = "signal-intro";
			dialog.setAttribute("aria-labelledby", "signal-intro-title");
			dialog.innerHTML = `
				<p class="visually-hidden" id="signal-intro-title">Night Vision unlocked.</p>
				<div class="signal-screen" aria-hidden="true">
					<div class="signal-bar"><span>Brothers in Arms // Secure Terminal</span><span data-signal-clock></span></div>
					<div class="signal-log"></div>
					<div class="signal-foot"><span>Channel Encrypted · Node BIA-01</span></div>
					<div class="signal-stamp">
						<span class="signal-eyebrow">Clearance Confirmed</span>
						<strong class="signal-title">Access Granted</strong>
						<span class="signal-note">Night Vision unlocked. Welcome to the night shift, brother.</span>
					</div>
				</div>
				<button class="signal-skip" type="button" autofocus>Skip Intro</button>`;
			const log = dialog.querySelector(".signal-log");
			const opened = performance.now();
			let ended = false;

			const finish = () => {
				if (!dialog.isConnected) return;
				if (dialog.open) dialog.close();
				dialog.remove();
				state.intro = null;
				resolve();
			};

			const end = () => {
				if (ended) return;
				ended = true;
				dialog.classList.add("is-closing");
				dialog.addEventListener("animationend", (event) => {
					if (event.target === dialog && event.animationName.startsWith("signal-intro-off")) finish();
				});
				setTimeout(finish, 900); // animationend never fires while the tab is hidden
			};

			// The last key of the word may still be followed by a few: skipping starts a moment later.
			const skip = () => {
				if (performance.now() - opened > 600) end();
			};

			dialog.addEventListener("keydown", (event) => {
				if (!["Shift", "Control", "Alt", "Meta", "Tab"].includes(event.key)) skip();
			});
			dialog.addEventListener("click", skip);
			dialog.addEventListener("cancel", (event) => {
				event.preventDefault();
				skip();
			});
			dialog.addEventListener("close", finish);

			const pause = (ms) => wait(ended ? 0 : ms);
			// Reduced motion: whole lines instead of typed letters, scrambles and a filling meter.
			const calm = reducedMotion.matches;

			const line = (className, markup) => {
				const element = document.createElement("div");
				element.className = className;
				element.innerHTML = markup;
				log.append(element);
				return element;
			};

			// The caret is the last character of the same text node: a caret element moving along with
			// the text would count as a layout shift in Chromium.
			const typeCommand = async (text) => {
				const target = line("signal-cmd", '<span class="signal-prompt">&gt;</span> <span class="signal-typed"></span>').lastChild;
				for (let index = calm ? text.length : 1; index <= text.length; index += 1) {
					if (ended) return;
					target.textContent = `${text.slice(0, index)}▌`;
					await pause(text[index - 1] === " " ? 55 : 16 + (text.charCodeAt(index - 1) % 3) * 6);
				}
				await pause(180);
				target.textContent = text;
			};

			const GLYPHS = "ABCDEFGHJKLMNPQRSTUVWXYZ0123456789#%&";
			const scramble = async (target, text) => {
				for (let frame = 0; frame < 6 && !ended && !calm; frame += 1) {
					target.textContent = Array.from(text, () => GLYPHS[Math.floor(Math.random() * GLYPHS.length)]).join("");
					await pause(34);
				}
				target.textContent = text;
			};

			const showRow = async (label, status) => {
				// The status starts as a no-break space: an empty span has no baseline and would drop into line.
				const row = line("signal-row", '<span class="signal-label"></span><span class="signal-dots"></span><span class="signal-status">&nbsp;</span>');
				row.querySelector(".signal-label").textContent = label;
				await pause(200);
				await scramble(row.querySelector(".signal-status"), status);
				await pause(90);
			};

			const showGain = async (label, level) => {
				const row = line(
					"signal-row signal-gain",
					'<span class="signal-label"></span><span class="signal-meter"><i></i></span><span class="signal-status">0%</span>',
				);
				row.querySelector(".signal-label").textContent = label;
				const status = row.querySelector(".signal-status");
				for (let value = calm ? level : 0; value <= level; value += 4) {
					row.style.setProperty("--level", value);
					status.textContent = `${value}%`;
					await pause(22);
				}
				row.style.setProperty("--level", level);
				status.textContent = `${level}%`;
				await pause(140);
			};

			const run = async () => {
				await pause(520);
				for (const [kind, text, value] of STEPS) {
					if (ended) return;
					if (kind === "cmd") await typeCommand(text);
					else if (kind === "row") await showRow(text, value);
					else await showGain(text, value);
				}
				if (ended) return;
				await pause(320);
				dialog.classList.add("is-granted");
				await pause(2300);
				end();
			};

			document.body.append(dialog);
			state.intro = dialog;
			tick();
			dialog.showModal();
			run();
		});
	}

	/* ---------- Music ---------- */

	const sound = { element: null, context: null, gain: null, on: false, armed: false, fade: 0, halt: 0 };

	function soundElement() {
		if (sound.element) return sound.element;
		const element = new Audio();
		element.crossOrigin = "anonymous";
		element.loop = true;
		element.preload = "auto";
		element.src = assets.signalAudio;
		const time = Number(store.get("time"));
		if (time > 0) element.currentTime = time;
		// iOS keeps an element's volume at 1: there the level goes through a gain node instead.
		element.volume = 0.5;
		if (element.volume !== 0.5 && "AudioContext" in window) {
			try {
				const context = new AudioContext();
				const gain = context.createGain();
				gain.gain.value = 0;
				context.createMediaElementSource(element).connect(gain).connect(context.destination);
				Object.assign(sound, { context, gain });
			} catch {
				// Full volume is better than no music.
			}
		}
		element.volume = 0;
		// A pause nobody here asked for: WebKit starts playback without a gesture, then stops it.
		element.addEventListener("pause", () => {
			if (!sound.on || document.hidden) return;
			sound.on = false;
			arm();
			renderHud();
		});
		sound.element = element;
		return element;
	}

	function fadeTo(level, seconds) {
		clearInterval(sound.fade);
		if (sound.gain) {
			const { gain, context } = sound;
			gain.gain.cancelScheduledValues(context.currentTime);
			gain.gain.setValueAtTime(gain.gain.value, context.currentTime);
			gain.gain.linearRampToValueAtTime(level, context.currentTime + seconds);
			return;
		}
		const element = sound.element;
		const from = element.volume;
		const begin = performance.now();
		sound.fade = setInterval(() => {
			const progress = Math.min(1, (performance.now() - begin) / (seconds * 1000));
			element.volume = from + (level - from) * progress;
			if (progress === 1) clearInterval(sound.fade);
		}, 40);
	}

	// Resolves true once the music is audible. Without a gesture the browser may refuse: false.
	async function playSound() {
		const element = soundElement();
		clearTimeout(sound.halt);
		const resumed = sound.context ? Promise.race([sound.context.resume(), wait(600)]) : null;
		try {
			await Promise.all([element.play(), resumed]);
		} catch {
			return false;
		}
		// WebKit may resolve play() and still not play without a gesture.
		if (element.paused || (sound.context && sound.context.state !== "running")) {
			element.pause();
			return false;
		}
		fadeTo(VOLUME, 2.4);
		return true;
	}

	function pauseSound(seconds) {
		const element = sound.element;
		if (!element || element.paused) return;
		clearTimeout(sound.halt);
		if (document.hidden || !seconds) {
			// Silent at once, so the next play fades in again.
			clearInterval(sound.fade);
			if (sound.gain) sound.gain.gain.setValueAtTime(0, sound.context.currentTime);
			else element.volume = 0;
			element.pause();
			return;
		}
		fadeTo(0, seconds);
		sound.halt = setTimeout(() => element.pause(), seconds * 1000);
	}

	function startMusic() {
		disarm();
		playSound().then((playing) => {
			if (!state.token) {
				if (playing) pauseSound(0.3);
				return;
			}
			sound.on = playing && !sound.element.paused;
			if (!sound.on) arm();
			renderHud();
		});
	}

	function pauseMusic() {
		store.set("music", "off");
		sound.on = false;
		disarm();
		pauseSound(0.6);
		renderHud();
	}

	function stopMusic() {
		sound.on = false;
		disarm();
		pauseSound(0.8);
	}

	// Blocked autoplay (a shared link, a new page in Safari): the first interaction starts it.
	function onInteraction(event) {
		if (!event.target.closest?.(".signal-hud")) startMusic();
	}

	function arm() {
		if (sound.armed) return;
		sound.armed = true;
		for (const type of ["pointerup", "keydown"]) addEventListener(type, onInteraction, true);
	}

	function disarm() {
		if (!sound.armed) return;
		sound.armed = false;
		for (const type of ["pointerup", "keydown"]) removeEventListener(type, onInteraction, true);
	}

	document.addEventListener("visibilitychange", () => {
		if (!state.token || !sound.on) return;
		if (document.hidden) pauseSound(0);
		else startMusic();
	});

	/* ---------- Page lifecycle ---------- */

	addEventListener("pagehide", () => {
		if (state.token && sound.element) store.set("time", String(sound.element.currentTime));
	});

	// Back/forward cache: follow what happened on the other pages meanwhile.
	addEventListener("pageshow", (event) => {
		if (!event.persisted) return;
		const token = store.get("token");
		if (!token && state.token) deactivate();
		else if (token && !state.token && isToken(token)) activate(token);
		else if (state.token && sound.on) startMusic();
	});

	// Filters and variants rewrite the URL through the History API: put the callsign back.
	globalThis.navigation?.addEventListener("currententrychange", () => {
		if (state.token) syncUrl();
	});

	const fromUrl = new URLSearchParams(location.search).get(PARAM);
	const fromSession = store.get("token");
	const token = [fromSession, fromUrl].find(isToken);
	if (token) activate(token, { warm: token !== fromSession });
}
